import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { registerDetachedWindow } from "@shared/utils/detachedWindowRegistry"

const POPUP_NAME = "SigmaInquiryDetached"
const POPUP_ROOT_ID = "inquiry-detached-root"
const POPUP_BASE_STYLE_ID = "inquiry-detached-base-style"
const POPUP_BASE_HREF_ID = "inquiry-detached-base-href"
const POPUP_STYLE_CLONE_ATTR = "data-inquiry-popup-style-clone"
const DEFAULT_POPUP_WIDTH = 450
const DEFAULT_POPUP_HEIGHT = 768
const POPUP_CLOSE_WATCH_INTERVAL_MS = 300

interface PopupLayout {
  width: number
  height: number
  left: number
  top: number
}

interface ScreenWithPlacement extends Screen {
  availLeft?: number
  availTop?: number
}

interface InquiryDetachedPortalProps {
  open: boolean
  title?: string
  activationKey?: number
  reservedPopupWindow?: Window | null
  onClose: () => void
  onOpenFailed?: () => void
  children: React.ReactNode
}

interface PopupWindowLike extends Window {
  restore?: () => void
  show?: () => void
  moveTop?: () => void
}

const normalizePopupLayoutNumber = (value: unknown, fallback: number) => {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value) : fallback
}

const getCurrentScreenRect = () => {
  if (typeof window === "undefined") {
    return {
      left: 0,
      top: 0,
      width: DEFAULT_POPUP_WIDTH,
      height: DEFAULT_POPUP_HEIGHT,
    }
  }

  const screenWithPlacement = window.screen as ScreenWithPlacement
  const width = Math.max(240, window.screen.availWidth)
  const height = Math.max(240, window.screen.availHeight)

  return {
    left: normalizePopupLayoutNumber(screenWithPlacement.availLeft, 0),
    top: normalizePopupLayoutNumber(screenWithPlacement.availTop, 0),
    width,
    height,
  }
}

const clampPopupLayoutToCurrentScreen = (layout: PopupLayout): PopupLayout => {
  const currentScreenRect = getCurrentScreenRect()
  const width = Math.min(layout.width, Math.max(240, currentScreenRect.width - 24))
  const height = Math.min(layout.height, Math.max(240, currentScreenRect.height - 24))
  const maxLeft = currentScreenRect.left + Math.max(0, currentScreenRect.width - width)
  const maxTop = currentScreenRect.top + Math.max(0, currentScreenRect.height - height)

  return {
    width,
    height,
    left: Math.min(maxLeft, Math.max(currentScreenRect.left, layout.left)),
    top: Math.min(maxTop, Math.max(currentScreenRect.top, layout.top)),
  }
}

const getDefaultPopupLayout = (): PopupLayout => {
  if (typeof window === "undefined") {
    return {
      width: DEFAULT_POPUP_WIDTH,
      height: DEFAULT_POPUP_HEIGHT,
      left: 0,
      top: 0,
    }
  }

  const currentScreenRect = getCurrentScreenRect()
  const availableWidth = Math.max(240, currentScreenRect.width - 48)
  const availableHeight = Math.max(240, currentScreenRect.height - 48)
  const width = Math.min(DEFAULT_POPUP_WIDTH, availableWidth)
  const height = Math.min(DEFAULT_POPUP_HEIGHT, availableHeight)
  const left = Math.round(window.screenX + Math.max(24, (window.outerWidth - width) / 2))
  const top = Math.round(window.screenY + Math.max(24, (window.outerHeight - height) / 2))

  return clampPopupLayoutToCurrentScreen({
    width,
    height,
    left,
    top,
  })
}

const isPopupLayoutVisibleOnScreen = (layout: PopupLayout, screenRect: PopupLayout) => {
  const screenRight = screenRect.left + screenRect.width
  const screenBottom = screenRect.top + screenRect.height
  const visibleWidth = Math.min(layout.left + layout.width, screenRight) - Math.max(layout.left, screenRect.left)
  const visibleHeight = Math.min(layout.top + layout.height, screenBottom) - Math.max(layout.top, screenRect.top)

  return (
    visibleWidth >= Math.min(240, Math.max(120, Math.floor(layout.width * 0.2))) &&
    visibleHeight >= Math.min(180, Math.max(80, Math.floor(layout.height * 0.2)))
  )
}

const isPopupLayoutVisibleOnCurrentScreen = (layout: PopupLayout) => {
  return isPopupLayoutVisibleOnScreen(layout, getCurrentScreenRect())
}

const readPopupWindowLayout = (popupWindow: Window): PopupLayout | null => {
  try {
    if (popupWindow.closed) return null

    return {
      width: Math.max(240, normalizePopupLayoutNumber(popupWindow.outerWidth, DEFAULT_POPUP_WIDTH)),
      height: Math.max(240, normalizePopupLayoutNumber(popupWindow.outerHeight, DEFAULT_POPUP_HEIGHT)),
      left: normalizePopupLayoutNumber(popupWindow.screenX, 0),
      top: normalizePopupLayoutNumber(popupWindow.screenY, 0),
    }
  } catch (error) {
    console.debug("문의하기 분리창 현재 위치 읽기 실패", error)
    return null
  }
}

const buildPopupFeatures = () => {
  const { width, height, left, top } = getDefaultPopupLayout()
  return ["popup=yes", `width=${width}`, `height=${height}`, `left=${left}`, `top=${top}`, "resizable=yes", "scrollbars=no"].join(",")
}

export const openReservedInquiryDetachedPopup = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.open("", POPUP_NAME, buildPopupFeatures())
}

const applyPopupLayout = (popupWindow: Window, layout: PopupLayout = getDefaultPopupLayout()) => {
  const { width, height, left, top } = layout

  try {
    popupWindow.resizeTo(width, height)
  } catch (error) {
    console.debug("문의하기 분리창 크기 초기화 실패", error)
  }

  try {
    popupWindow.moveTo(left, top)
  } catch (error) {
    console.debug("문의하기 분리창 위치 초기화 실패", error)
  }
}

const getStyleElementCssText = (styleElement: HTMLStyleElement) => {
  try {
    const cssRules = styleElement.sheet?.cssRules
    if (cssRules && cssRules.length > 0) {
      return Array.from(cssRules)
        .map(rule => rule.cssText)
        .join("\n")
    }
  } catch {
    return styleElement.textContent || ""
  }

  return styleElement.textContent || ""
}

const cloneStyleNode = (node: Element, targetDocument: Document) => {
  if (node instanceof HTMLLinkElement) {
    const clonedLink = targetDocument.createElement("link")
    Array.from(node.attributes).forEach(attribute => {
      clonedLink.setAttribute(attribute.name, attribute.value)
    })
    clonedLink.href = node.href
    clonedLink.setAttribute(POPUP_STYLE_CLONE_ATTR, "true")
    targetDocument.head.appendChild(clonedLink)
    return
  }

  if (node instanceof HTMLStyleElement) {
    const clonedStyle = targetDocument.createElement("style")
    Array.from(node.attributes).forEach(attribute => {
      clonedStyle.setAttribute(attribute.name, attribute.value)
    })
    clonedStyle.textContent = getStyleElementCssText(node)
    clonedStyle.setAttribute(POPUP_STYLE_CLONE_ATTR, "true")
    targetDocument.head.appendChild(clonedStyle)
  }
}

const syncPopupDocumentStyles = (targetDocument: Document) => {
  if (typeof window === "undefined") return () => {}

  const sourceHead = window.document.head
  const targetWindow = targetDocument.defaultView || window
  let scheduledFrame: number | null = null

  const copyStyles = () => {
    targetDocument.head.querySelectorAll(`[${POPUP_STYLE_CLONE_ATTR}]`).forEach(node => node.remove())

    sourceHead.querySelectorAll('style, link[rel="stylesheet"]').forEach(node => {
      cloneStyleNode(node, targetDocument)
    })
  }

  const scheduleCopyStyles = () => {
    if (scheduledFrame !== null) return

    scheduledFrame = targetWindow.requestAnimationFrame(() => {
      scheduledFrame = null
      copyStyles()
    })
  }

  copyStyles()

  const observer = new MutationObserver(() => {
    scheduleCopyStyles()
  })

  observer.observe(sourceHead, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
  })

  return () => {
    observer.disconnect()
    if (scheduledFrame !== null) {
      targetWindow.cancelAnimationFrame(scheduledFrame)
      scheduledFrame = null
    }
    targetDocument.head.querySelectorAll(`[${POPUP_STYLE_CLONE_ATTR}]`).forEach(node => node.remove())
  }
}

const ensurePopupBaseDocument = (popupWindow: Window, title: string) => {
  const popupDocument = popupWindow.document
  popupDocument.title = title

  const sourceBaseHref = typeof window === "undefined" ? popupDocument.baseURI : window.document.baseURI

  let baseHref = popupDocument.getElementById(POPUP_BASE_HREF_ID) as HTMLBaseElement | null
  if (!baseHref) {
    baseHref = popupDocument.createElement("base")
    baseHref.id = POPUP_BASE_HREF_ID
    popupDocument.head.prepend(baseHref)
  }
  baseHref.href = sourceBaseHref

  let baseStyle = popupDocument.getElementById(POPUP_BASE_STYLE_ID) as HTMLStyleElement | null
  if (!baseStyle) {
    baseStyle = popupDocument.createElement("style")
    baseStyle.id = POPUP_BASE_STYLE_ID
    popupDocument.head.appendChild(baseStyle)
  }

  baseStyle.textContent = `
    html, body, #${POPUP_ROOT_ID} {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      overflow: hidden;
      background: #ffffff;
    }

    body {
      font-family: 'Noto Sans KR', 'Malgun Gothic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
  `

  let mountNode = popupDocument.getElementById(POPUP_ROOT_ID) as HTMLDivElement | null
  if (!mountNode) {
    mountNode = popupDocument.createElement("div")
    mountNode.id = POPUP_ROOT_ID
    popupDocument.body.appendChild(mountNode)
  }

  return mountNode
}

const InquiryDetachedPortal: React.FC<InquiryDetachedPortalProps> = ({
  open,
  title = "문의하기",
  activationKey = 0,
  reservedPopupWindow = null,
  onClose,
  onOpenFailed,
  children,
}) => {
  const popupWindowRef = useRef<Window | null>(null)
  const popupCacheRef = useRef<ReturnType<typeof createCache> | null>(null)
  const cleanupStylesRef = useRef<(() => void) | null>(null)
  const closingProgrammaticallyRef = useRef(false)
  const closeHandledRef = useRef(false)
  const skipInitialActivationRef = useRef(false)
  const lastSavedLayoutRef = useRef<string | null>(null)
  const latestTitleRef = useRef(title)
  const latestOnCloseRef = useRef(onClose)
  const latestOnOpenFailedRef = useRef(onOpenFailed)
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null)
  const closeWatchTimerRef = useRef<number | null>(null)

  latestTitleRef.current = title
  latestOnCloseRef.current = onClose
  latestOnOpenFailedRef.current = onOpenFailed

  const activatePopupWindow = React.useCallback((popupWindow: Window) => {
    if (popupWindow.closed) {
      return
    }

    const popupWindowLike = popupWindow as PopupWindowLike

    const finalizeActivation = () => {
      const currentLayout = readPopupWindowLayout(popupWindow)
      const nextLayout =
        currentLayout && isPopupLayoutVisibleOnCurrentScreen(currentLayout) ? clampPopupLayoutToCurrentScreen(currentLayout) : getDefaultPopupLayout()
      const serialized = JSON.stringify(nextLayout)

      applyPopupLayout(popupWindow, nextLayout)
      lastSavedLayoutRef.current = serialized

      try {
        popupWindowLike.moveTop?.()
      } catch (error) {
        console.debug("문의하기 분리창 최상단 이동 실패", error)
      }

      try {
        popupWindow.focus()
      } catch (error) {
        console.debug("문의하기 분리창 활성화 실패", error)
      }
    }

    const fallbackRestore = () => {
      try {
        popupWindowLike.restore?.()
      } catch (error) {
        console.debug("문의하기 분리창 restore 직접 호출 실패", error)
      }

      try {
        popupWindowLike.show?.()
      } catch (error) {
        console.debug("문의하기 분리창 show 직접 호출 실패", error)
      }

      finalizeActivation()
    }

    const openerRestore = window.electronAPI?.restoreWindowByTitle
    if (openerRestore) {
      void openerRestore(latestTitleRef.current)
        .catch(error => {
          console.debug("문의하기 분리창 제목 기준 복원 IPC 호출 실패", error)
          return false
        })
        .then(restored => {
          if (restored) {
            finalizeActivation()
            return
          }

          if (popupWindowLike.electronAPI?.restore) {
            void popupWindowLike.electronAPI
              .restore()
              .catch(error => {
                console.debug("문의하기 분리창 복원 IPC 호출 실패", error)
                fallbackRestore()
              })
              .then(result => {
                if (result === undefined) {
                  return
                }

                if (result) {
                  finalizeActivation()
                  return
                }

                fallbackRestore()
              })
            return
          }

          fallbackRestore()
        })
      return
    }

    if (popupWindowLike.electronAPI?.restore) {
      void popupWindowLike.electronAPI
        .restore()
        .catch(error => {
          console.debug("문의하기 분리창 복원 IPC 호출 실패", error)
          fallbackRestore()
        })
        .then(result => {
          if (result === undefined) {
            return
          }

          if (result) {
            finalizeActivation()
            return
          }

          fallbackRestore()
        })
      return
    }

    fallbackRestore()
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    const popupWindow = popupWindowRef.current
    if (!popupWindow || popupWindow.closed) {
      return
    }

    popupWindow.document.title = title
  }, [open, title])

  useEffect(() => {
    if (typeof window === "undefined") return
    closingProgrammaticallyRef.current = false

    const clearCloseWatchTimer = () => {
      if (closeWatchTimerRef.current === null) {
        return
      }

      window.clearInterval(closeWatchTimerRef.current)
      closeWatchTimerRef.current = null
    }

    if (!open) {
      closeHandledRef.current = false
      clearCloseWatchTimer()
      cleanupStylesRef.current?.()
      cleanupStylesRef.current = null

      const popupWindow = popupWindowRef.current
      if (popupWindow && !popupWindow.closed) {
        closingProgrammaticallyRef.current = true
        popupWindow.close()
      }

      skipInitialActivationRef.current = false
      popupWindowRef.current = null
      popupCacheRef.current = null
      lastSavedLayoutRef.current = null
      setMountNode(null)
      closingProgrammaticallyRef.current = false
      return
    }

    const existingPopup = popupWindowRef.current
    if (existingPopup && !existingPopup.closed) {
      activatePopupWindow(existingPopup)
      return
    }

    closeHandledRef.current = false

    const popupWindow = reservedPopupWindow && !reservedPopupWindow.closed ? reservedPopupWindow : window.open("", POPUP_NAME, buildPopupFeatures())
    if (!popupWindow) {
      latestOnOpenFailedRef.current?.()
      return
    }

    popupWindowRef.current = popupWindow
    registerDetachedWindow("inquiry", popupWindow, title)
    const popupMountNode = ensurePopupBaseDocument(popupWindow, title)
    cleanupStylesRef.current = syncPopupDocumentStyles(popupWindow.document)
    popupCacheRef.current = createCache({
      key: "inquiry-popup",
      container: popupWindow.document.head,
      speedy: false,
    })
    const initialLayout = getDefaultPopupLayout()
    applyPopupLayout(popupWindow, initialLayout)
    lastSavedLayoutRef.current = JSON.stringify(initialLayout)
    skipInitialActivationRef.current = true
    setMountNode(popupMountNode)

    const syncPopupLayout = () => {
      const nextLayout = readPopupWindowLayout(popupWindow)
      if (!nextLayout) return

      const serialized = JSON.stringify(nextLayout)
      if (lastSavedLayoutRef.current === serialized) return

      lastSavedLayoutRef.current = serialized
    }

    const cleanupPopupState = () => {
      clearCloseWatchTimer()
      cleanupStylesRef.current?.()
      cleanupStylesRef.current = null
      popupWindowRef.current = null
      popupCacheRef.current = null
      lastSavedLayoutRef.current = null
      setMountNode(null)
    }

    const handlePopupClosed = () => {
      const isProgrammaticClose = closingProgrammaticallyRef.current
      closingProgrammaticallyRef.current = false

      syncPopupLayout()
      cleanupPopupState()

      if (isProgrammaticClose || closeHandledRef.current) {
        return
      }

      closeHandledRef.current = true
      latestOnCloseRef.current()
    }

    closeWatchTimerRef.current = window.setInterval(() => {
      if (popupWindow.closed) {
        handlePopupClosed()
      }
    }, POPUP_CLOSE_WATCH_INTERVAL_MS)

    popupWindow.addEventListener("beforeunload", handlePopupClosed)
    popupWindow.addEventListener("resize", syncPopupLayout)
    popupWindow.addEventListener("move", syncPopupLayout)

    try {
      ;(popupWindow as PopupWindowLike).moveTop?.()
    } catch (error) {
      console.debug("문의하기 분리창 초기 최상단 이동 실패", error)
    }

    try {
      popupWindow.focus()
    } catch (error) {
      console.debug("문의하기 분리창 초기 포커스 실패", error)
    }

    return () => {
      syncPopupLayout()
      clearCloseWatchTimer()
      popupWindow.removeEventListener("beforeunload", handlePopupClosed)
      popupWindow.removeEventListener("resize", syncPopupLayout)
      popupWindow.removeEventListener("move", syncPopupLayout)
      cleanupStylesRef.current?.()
      cleanupStylesRef.current = null

      if (!popupWindow.closed) {
        closingProgrammaticallyRef.current = true
        popupWindow.close()
      }

      skipInitialActivationRef.current = false
      popupWindowRef.current = null
      popupCacheRef.current = null
      lastSavedLayoutRef.current = null
      setMountNode(null)
      closingProgrammaticallyRef.current = false
      closeHandledRef.current = false
    }
  }, [activatePopupWindow, open, reservedPopupWindow])

  useEffect(() => {
    if (!open) {
      return
    }

    if (skipInitialActivationRef.current) {
      skipInitialActivationRef.current = false
      return
    }

    const popupWindow = popupWindowRef.current
    if (!popupWindow || popupWindow.closed) {
      return
    }

    activatePopupWindow(popupWindow)
  }, [activatePopupWindow, activationKey, open])

  if (!open || !mountNode || !popupCacheRef.current) return null

  return createPortal(<CacheProvider value={popupCacheRef.current}>{children}</CacheProvider>, mountNode)
}

export default InquiryDetachedPortal
